import {clamp, padNum, performNextTick, random, texture} from "p3";
import definitions from "../../../definitions";
import GameScene from "../../../scenes/GameScene";
import Enemy from "./Enemy";
import common from "../../../common";

export default class EnemyCrabHostile extends Enemy {
    constructor() {
        super(definitions.ENEMY_ID.CRAB_HOSTILE);

        this.moveSpeed = 600;
        this.linearDamping = 6;
        this.collisionDamping = 12;

        this.collisionRadius = 12;
        this.collisionOffsetX = -5;
        this.collisionOffsetY = -12;

        this.moveToTarget = null;
        this.moveToTargetOffset = new PIXI.Point();
        this.moveToTargetMinRadius = 80;
        this.moveToTargetMaxRadius = 220;

        this.moveAwayFromTargetMinRadius = 0;
        this.moveAwayFromTargetMaxRadius = 600;

        this._wanderTime = 0;
        this._wanderDelay = 0;
        this._wanderDelayMin = 4.5;
        this._wanderDelayMax = 8;
        this._wanderSpeed = 600;

        this._chaseSpeed = 1400;
        this._chaseRange = 300;

        this._attackSpeed = 200;
        this._attackRange = 100;
        this._attackTime = 0;
        this._attackDelay = 2;
        this._isAttacking = false;

        this._retreatSpeed = 1800;

        this._takeDamageSpeed = 0;
        this._isTakingDamage = false;

        this._avoidNeighbourMinRange = 140;
        this._avoidNeighbourWeight = 0.6;

        this._defaultTween = null;
    }

    init() {
        super.init();

        let frames = [];
        for (let i = 0; i < 8; ++i) {
            frames[i] = texture("enemy_crab-idle_00_" + padNum(i, 1));
        }
        const sequence = new P3.MovieClipSequence(frames, "idle");

        frames = [];
        for (let i = 0; i < 8; ++i) {
            frames[i] = texture("enemy_crab-move_00_" + padNum(i, 1));
        }
        sequence.addTextures(frames, "move");

        frames = [];
        for (let i = 0; i < 6; ++i) {
            frames[i] = texture("enemy_crab-attack_00_" + padNum(i, 1));
        }
        sequence.addTextures(frames, "attack");

        frames = [];
        for (let i = 0; i < 6; ++i) {
            frames[i] = texture("enemy_crab-take_hit_00_" + padNum(i, 1));
        }
        sequence.addTextures(frames, "take_damage");

        frames = [];
        for (let i = 0; i < 6; ++i) {
            frames[i] = texture("enemy_crab-defeat_00_" + padNum(i, 1));
        }
        sequence.addTextures(frames, "dead");

        this.addViewWithMovieClip(sequence, "idle");

        this.state = definitions.ENEMY_STATE.WANDER;

        this._attackTime = this._attackDelay;
    }

    destroy() {
        this._stopAnimations();

        super.destroy();
    }

    onViewAdded() {
        super.onViewAdded();

        this._view.anchor.set(0.5, 1);
        this._view.animationSpeed = 24;
        this._view.gotoAndPlay("idle", true);

        this._view.addFrameScript(4, () => this.onAttackDamage(), null, "attack");
        this._view.addFrameScript(5, () => this.onAttackFinished(), null, "attack");
        this._view.addFrameScript(5, () => this.onTakeDamageFinished(), null, "take_damage");

        this.addShadow(definitions.SHADOW_SIZE.SMALL, 1, new PIXI.Point(-20, -20));
    }

    update() {
        super.update();

        if (!this.disableKinematic) {
            if (this.moveToTarget) {
                const force = new P3.Vector2();
                const target = new PIXI.Point(this.moveToTarget.x + this.moveToTargetOffset.x, this.moveToTarget.y + this.moveToTargetOffset.y);

                if(this.target.isPlayer && common.saveData.activeSlot.player.effects && common.saveData.activeSlot.player.effects[definitions.EFFECTS.FRIGHTEN_ENEMIES]) {
                    this._moveAwayFromTarget(target, force);
                } else {
                    this._moveToTarget(target, force);
                }
                this.facingDirection = force.x;
                this._avoidNeighbours(force, this._avoidNeighbourMinRange, this._avoidNeighbourWeight);
                this.velocity.set(this.velocity.x + force.x * window.dt, this.velocity.y + force.y * window.dt);
            }
        }

        if (this.velocity.lengthSq > 150) {
            if (!this._isMoving) {
                this._isMoving = true;
                this.onMoveStart();
            }
        } else {
            if (this._isMoving) {
                this._isMoving = false;
                this.onMoveStop();
            }
        }
    }

    /**
     * @return {PIXI.Point}
     * @private
     */
    _findSafePosition() {
        return this.world.findSafePosition(
            new PIXI.Point(this._initialPosition.x, this._initialPosition.y),
            600,
            0,
            120,
            [new PIXI.Point(this.x, this.y)],
            20
        );
    }

    /**
     * @private
     */
    _startAttack() {
        if (!this._isAttacking) {
            this._attackTime = 0;
            this._isAttacking = true;
            this._view.gotoAndPlay("attack", false);

            P3.audio.playSound([
                "sfx_crab_attack_00",
                "sfx_crab_attack_01"
            ]);
        }
    }

    /**
     * @private
     */
    _startTakeDamage() {
        this._isTakingDamage = true;
        this._view.gotoAndPlay("take_damage", false);
    }

    /**
     * @private
     */
    _startDead() {
        this._view.gotoAndPlay("dead", false);
        P3.audio.playSound("sfx_crab_die_00");
    }

    /**
     * @return {boolean}
     * @private
     */
    _isTargetInChaseRange() {
        if (this.target && (GameScene.active.activeMinigame == null || !GameScene.active.activeMinigame.repressEnemyChase)) {
            const to = new P3.Vector2(this.target.x - this.x, this.target.y - this.y);
            return to.lengthSq < this._chaseRange ** 2;
        }
        return false;
    }

    /**
     * @return {boolean}
     * @private
     */
    _isTargetInAttackRange() {
        if (this.target) {
            const to = new P3.Vector2(this.target.x - this.x, this.target.y - this.y);
            return to.lengthSq < this._attackRange ** 2;
        }
        return false;
    }

    /**
     * @param {object} target
     * @param {P3.Vector2} force
     * @private
     */
    _moveToTarget(target, force) {
        let move = true;

        force.set(target.x - this.x, target.y - this.y);
        const distance = force.length;
        force.x /= distance;
        force.y /= distance;

        let targetSpeed = Math.min(1, (distance / this.moveToTargetMaxRadius)) * this.moveSpeed;
        if (distance < this.moveToTargetMinRadius) {
            move = false;
            targetSpeed = 0;
        }
        force.scaleBy(targetSpeed);
        return move;
    }

    /**
     * @param {object} target
     * @param {P3.Vector2} force
     * @private
     */
    _moveAwayFromTarget(target, force) {
        let move = true;

        force.set(this.x - target.x, this.y - target.y);
        const distance = force.length;
        //const inverse = Math.min(this.moveAwayFromTargetMaxRadius, Math.max(this.moveAwayFromTargetMinRadius, this.moveAwayFromTargetMaxRadius - force.length));
        force.x /= distance;
        force.y /= distance;

        let targetSpeed = Math.max(0, Math.min(1, 1-(distance / this.moveAwayFromTargetMaxRadius))) * this.moveSpeed;
        if (distance < this.moveAwayFromTargetMinRadius) {
            move = false;
            targetSpeed = 0;
        }
        force.scaleBy(targetSpeed);
        return move;
    }

    /**
     * @param {P3.Vector2} force
     * @param {number} minRange
     * @param {number} [weight = 0.5]
     * @private
     */
    _avoidNeighbours(force, minRange, weight = 0.5) {
        weight = Math.max(0, weight);
        for (let i = 0, count = this.neighbours.length; i < count; ++i) {
            const neighbour = this.neighbours[i];
            if (this !== neighbour) {
                if (!(this.x === neighbour.x && this.y === neighbour.y)) {
                    const to = new P3.Vector2(this.x - neighbour.x, this.y - neighbour.y);
                    const distance = to.length;
                    if (distance < minRange) {
                        const speed = (1 - clamp(distance / minRange, 0, 1)) * minRange;
                        to.x /= distance;
                        to.y /= distance;
                        force.x += to.x * speed * weight;
                        force.y += to.y * speed * weight;
                    }
                }
            }
        }
    }

    /**
     * @private
     */
    _stopAnimations() {
        if (this._defaultTween) {
            GameScene.active.removeTween(this._defaultTween, true);
            this._defaultTween = null;
        }
    }

    onMoveStart() {
        if (!this.isAttacking && !this.isTakingDamage && !this.isDead) {
            this._view.gotoAndPlay("move", true);
        }
    }

    onMoveStop() {
        if (!this.isAttacking && !this.isTakingDamage && !this.isDead) {
            this._view.gotoAndPlay("idle", true);
        }
    }

    /**
     * @param {Entity} entity
     * @param {P3.Vector2} v
     */
    onCollisionEntity(entity, v) {
        super.onCollisionEntity(entity, v);

        this.x += v.x;
        this.y += v.y;

        v.normalize();

        const d = 1 - Math.exp(-this.collisionDamping * window.dt);

        this.velocity.x *= 1 - Math.abs(v.x) * d;
        this.velocity.y *= 1 - Math.abs(v.y) * d;
    }

    /**
     * @param {Tile} tile
     * @param {P3.Vector2} v
     */
    onCollisionTile(tile, v) {
        super.onCollisionTile(tile, v);

        this.x += v.x;
        this.y += v.y;

        v.normalize();

        const d = 1 - Math.exp(-this.collisionDamping * window.dt);

        this.velocity.x *= 1 - Math.abs(v.x) * d;
        this.velocity.y *= 1 - Math.abs(v.y) * d;
    }

    /**
     * @param {number} state
     * @private
     */
    onStateEnter(state) {
        switch (state) {
            case definitions.ENEMY_STATE.IDLE: {
                this.moveSpeed = this._wanderSpeed;
                this.moveToTarget = null;
                break;
            }
            case definitions.ENEMY_STATE.WANDER: {
                this._wanderTime = 0;
                this._wanderDelay = random(this._wanderDelayMin, this._wanderDelayMax);

                this.moveSpeed = this._wanderSpeed;
                this.moveToTarget = this._findSafePosition();
                break;
            }
            case definitions.ENEMY_STATE.CHASE: {
                this.moveSpeed = this._chaseSpeed;
                this.moveToTarget = this.target;
                break;
            }
            case definitions.ENEMY_STATE.RETREAT: {
                this.moveSpeed = this._retreatSpeed;
                this.moveToTarget = this._findSafePosition();
                break;
            }
            case definitions.ENEMY_STATE.ATTACK: {
                this.moveSpeed = this._attackSpeed;
                this.moveToTarget = this.target;
                break;
            }
            case definitions.ENEMY_STATE.TAKE_DAMAGE: {
                this.moveSpeed = this._takeDamageSpeed;
                this._startTakeDamage();
                break;
            }
            case definitions.ENEMY_STATE.DEAD: {
                this.moveSpeed = 0;
                this._startDead();
                break;
            }
        }
    }

    /**
     * @param {number} state
     * @private
     */
    onStateUpdate(state) {
        switch (state) {
            case definitions.ENEMY_STATE.IDLE: {
                break;
            }
            case definitions.ENEMY_STATE.WANDER:
            case definitions.ENEMY_STATE.RETREAT: {
                if (this.moveToTarget) {
                    if (this._wanderTime > this._wanderDelay) {
                        this.state = definitions.ENEMY_STATE.WANDER;
                    }
                    this._wanderTime += window.dt;
                }
                if (this._isTargetInChaseRange()) {
                    /*if (common.saveData.activeSlot.player.effects[definitions.EFFECTS.FRIGHTEN_ENEMIES]) {
                        if (this.state != definitions.ENEMY_STATE.RETREAT) {
                            this.state = definitions.ENEMY_STATE.RETREAT;
                        }
                    } else {*/
                        this.state = definitions.ENEMY_STATE.CHASE;
                    //}
                }
                break;
            }
            case definitions.ENEMY_STATE.CHASE: {
                if (!this._isTargetInChaseRange()){// || common.saveData.activeSlot.player.effects[definitions.EFFECTS.FRIGHTEN_ENEMIES]) {
                    this.state = definitions.ENEMY_STATE.RETREAT;
                }

                if (this.isAttackReady && this._isTargetInAttackRange()) {
                    this.state = definitions.ENEMY_STATE.ATTACK;
                }
                break;
            }
            case definitions.ENEMY_STATE.ATTACK: {
                if (!this.isAttacking && this.isAttackReady) {
                    this._startAttack();
                }
                break;
            }
            case definitions.ENEMY_STATE.TAKE_DAMAGE: {
                break;
            }
            case definitions.ENEMY_STATE.DEAD: {
                break;
            }
        }
        this._attackTime += window.dt;
    }

    /**
     * @param {number} state
     * @private
     */
    onStateExit(state) {
        switch (state) {
            case definitions.ENEMY_STATE.ATTACK: {
                this._isAttacking = false;
                break;
            }
            case definitions.ENEMY_STATE.TAKE_DAMAGE: {
                this._isTakingDamage = false;
                break;
            }
        }
    }

    onAttackDamage() {
        this._isTargetInAttackRange() && this.signals.attack.dispatch(this, this.target, this.damage);
    }

    onAttackFinished() {
        this._attackTime = 0;
        this._isAttacking = false;

        this.state = definitions.ENEMY_STATE.CHASE;

        performNextTick(() => {
            if (this._isMoving) {
                this.onMoveStart();
            } else {
                this.onMoveStop();
            }
        });
    }

    onTakeDamageFinished() {
        this.state = definitions.ENEMY_STATE.CHASE;

        performNextTick(() => {
            if (this._isMoving) {
                this.onMoveStart();
            } else {
                this.onMoveStop();
            }
        });
    }

    onDeadFinished() {
        this._view.visible = false;
    }

    /**
     * @param {number} damage
     */
    onTakeDamage(damage) {
    }

    onDead() {
        this._stopAnimations();

        this._defaultTween = gsap.timeline();
        GameScene.active.addTween(this._defaultTween);

        this._defaultTween.add(gsap.to(this._view.scale, 0.24, {
            x: 0,
            y: 0,
            ease: Back.easeIn,
            onComplete: () => {
                this._view.visible = false;
            }
        }), 0);
        this._defaultTween.add(gsap.to(this._shadow.scale, 0.24, {
            x: 0,
            y: 0,
            ease: Back.easeIn,
            onComplete: () => {
                this._shadow.visible = false;
            }
        }), 0);

        super.onDead();
    }

    onRespawn() {
        this._stopAnimations();

        this._defaultTween = gsap.timeline();
        GameScene.active.addTween(this._defaultTween);

        this._defaultTween.add(gsap.to(this._view.scale, 0.24, {
            x: 1,
            y: 1,
            ease: Back.easeOut,
            onStart: () => {
                this._view.visible = true;
            }
        }), 0);
        this._defaultTween.add(gsap.to(this._shadow.scale, 0.24, {
            x: 1,
            y: 1,
            ease: Back.easeOut,
            onStart: () => {
                this._shadow.visible = true;
            }
        }), 0);

        super.onRespawn();
    }

    /**
     * @return {number}
     */
    get wanderTime() {
        return this._wanderTime;
    }

    /**
     * @return {boolean}
     */
    get isAttacking() {
        return this._isAttacking;
    }

    /**
     * @return {boolean}
     */
    get isTakingDamage() {
        return this._isTakingDamage;
    }

    /**
     * @return {boolean}
     */
    get isAttackReady() {
        return this._attackTime >= this._attackDelay;
    }
}